'use client';

import { clearInstantRouteTransition } from '@/lib/instantRouteNavStore';
import ListPageLoadingShell from '@/components/ListPageLoadingShell';
import ProductLoadingSkeleton from '@/components/ProductLoadingSkeleton';
import { getInstantRouteTransitionKind, isStaleRenderedRoute } from '@/lib/instantRouteTransition';
import { usePathname } from 'next/navigation';
import { useEffect, Fragment, type ReactNode } from 'react';

/**
 * Next soft-nav updates the address bar before the new server tree commits.
 * Without this, the previous category H1/products stay on screen under the new URL.
 *
 * `contentPath` must come from the RSC that rendered this tree (layout/page params),
 * not from useParams() — those follow the URL immediately.
 */
export default function StaleRouteGuard({
  contentPath,
  children,
}: {
  contentPath: string;
  children: ReactNode;
}) {
  const pathname = usePathname() || '/';
  const stale = isStaleRenderedRoute(contentPath, pathname);

  useEffect(() => {
    if (stale) return;
    clearInstantRouteTransition();
  }, [stale, contentPath, pathname]);

  if (stale) {
    const kind = getInstantRouteTransitionKind(pathname);
    // /profilim, /sepetim, … never used listing skeletons. Painting one here
    // hides the destination until the previous RSC tree unmounts — first click
    // to Favorilerim looked empty, second click (chunk cached) appeared fine.
    if (!kind) {
      return <Fragment key={contentPath}>{children}</Fragment>;
    }
    return kind === 'product' ? (
      <div className="px-2 md:px-4 mt-4 mb-16 bg-hzc_beige_200">
        <ProductLoadingSkeleton />
      </div>
    ) : (
      <ListPageLoadingShell />
    );
  }

  return <Fragment key={contentPath}>{children}</Fragment>;
}
